import { usePolicyOnDashboard } from '@folio/utils/usePolicyOnDashboard';
import { usePolicyProductConfig } from '@folio/utils/usePolicyProductConfig';
import { Badge } from '../../../components/ui/badge';
import { useToast } from '../../../components/ui/use-toast';
import { DeactivatableButton } from '../../../components/features/DeactivatableButton';
import { expectedPolicyStatusConflictMessage } from '../../../components/features/PolicyStatus';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  branchControllerForkBranchWithoutPolicy,
  brokerageFirmControllerGetBrokerageFirmQueryOptions,
  getKorintErrorCode,
  FinalizeNewBusinessModuleRule,
  legacyPermissionsControllerAtMeQueryOptions,
  policyControllerConfirmPolicy,
  policyControllerPushPolicyEvent,
  usePolicyControllerGeneratePolicySignatureRequest,
  BranchDto,
  Customer,
  PaymentMethodDtoWithIsPreferred,
  RulePayer,
  Policy,
  SendContractButtonConfig,
  policyControllerGetPolicyQueryOptions,
  keyValueFieldsToObject,
  BrokerageFirm,
  PolicyDocumentsDto,
  policyControllerListPolicyDocumentsQueryOptions,
  derogationControllerCheckDerogationRequirementQueryOptions,
  PolicyDtoStatusEnumKey,
} from 'korint-sdk';
import { MailIcon, PenIcon, XIcon } from 'lucide-react';
import { useState, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useNavigate } from 'react-router-dom';
import { OtpPhoneNumberModal, type OtpPhoneNumberModalRef } from './OtpPhoneNumberModal';
import { useFeatureFlaggedRules } from '../useFeatureFlaggedRules';
import { Group } from '@folio/components/ui/flex';
import { branchFunnelPath } from '@folio/utils/branchFunnelPath';

type SendContractButtonProps = {
  rules: FinalizeNewBusinessModuleRule[];
  customer: Customer;
  customers: Customer[];
  payers: RulePayer[] | undefined;
  paymentMethods: PaymentMethodDtoWithIsPreferred[];
  hasUnacknowledgedAlerts: boolean;
  overriddenBranchId?: string;
};

function resolveRuleMessage(rule: FinalizeNewBusinessModuleRule, t: TFunction): string {
  return rule.id ? t(`finalizeNewBusiness.rules.${rule.id}.message`) : '';
}

function createValidationRules({
  workingBranch,
  policy,
  customer,
  customers,
  payers,
  documents,
  brokerageFirm,
  hasUnacknowledgedAlerts,
  paymentMethods,
  rules,
  expectedStatuses,
  t,
}: {
  workingBranch: BranchDto;
  policy: Policy;
  customer: Customer;
  customers: Customer[];
  payers: RulePayer[] | undefined;
  documents: PolicyDocumentsDto[];
  brokerageFirm?: BrokerageFirm;
  hasUnacknowledgedAlerts: boolean;
  paymentMethods: PaymentMethodDtoWithIsPreferred[];
  rules: FinalizeNewBusinessModuleRule[];
  expectedStatuses: PolicyDtoStatusEnumKey[];
  t: TFunction;
}) {
  const defaultRules = [
    {
      isValid: workingBranch.branchStatus !== 'BRANCH_CLOSED',
      message: t('finalizeNewBusiness.branchClosed'),
    },
    {
      isValid: expectedStatuses.includes(policy.status),
      message: expectedPolicyStatusConflictMessage(expectedStatuses, t),
    },
  ];

  // Unresolved payers fail the rule instead of judging a partial list
  const customRules = rules.map(rule => ({
    isValid:
      payers !== undefined &&
      rule.isValid({
        policy,
        customer,
        documents,
        hasUnacknowledgedAlerts,
        paymentMethods: paymentMethods ?? [],
        customers,
        payers,
        brokerageFirm,
      }),
    message: resolveRuleMessage(rule, t),
  }));

  return [...defaultRules, ...customRules];
}

export function SendContractButton({
  rules,
  customer,
  customers,
  payers,
  paymentMethods,
  hasUnacknowledgedAlerts,
  overriddenBranchId,
}: SendContractButtonProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { branches, policyOnMain, ...policyOnDashboard } = usePolicyOnDashboard();
  const finalizeNewBusinessModuleConfig = usePolicyProductConfig('finalizeNewBusinessModuleConfig');
  const productCustomPath = usePolicyProductConfig('productCustomPath');
  const toast = useToast();

  const branchId = overriddenBranchId ?? policyOnDashboard.workingBranch?.id;
  const workingBranch = branches?.data.find(({ id }) => id === branchId);

  const { data: policyOnBranch, refetch: refetchPolicyOnBranch } = useQuery({
    ...policyControllerGetPolicyQueryOptions(policyOnMain.id, { branchId: branchId! }),
    select: data => ({
      ...data,
      customFields: keyValueFieldsToObject(data.customFields ?? []),
    }),
    enabled: Boolean(branchId),
  });
  const policy = useMemo(() => {
    if (policyOnBranch) {
      return policyOnBranch;
    }
    return policyOnDashboard.policy;
  }, [policyOnBranch, policyOnDashboard.policy]);

  const mutate = async () => {
    await Promise.all([refetchPolicyOnBranch(), policyOnDashboard.mutate()]);
  };

  async function createSignatureRequest({
    policyId,
    branchId,
    config,
    generatePolicySignatureRequest,
  }: {
    policyId: string;
    branchId: string;
    config: SendContractButtonConfig;
    generatePolicySignatureRequest: (params: {
      policyId: string;
      data: {
        deliveryMode: 'EMAIL' | 'URL';
        isOtp: boolean;
        phoneNumber: string;
      };
      params: { branchId: string };
    }) => Promise<any>;
  }) {
    return async (phoneNumber: string): Promise<void> => {
      if (config.actionType !== 'signature') {
        throw new Error('createSignatureRequest requires a signature button config');
      }

      const { signature } = config;

      if (signature.signatureType === 'policyholderFunnel') {
        await policyControllerPushPolicyEvent(
          policyId,
          { eventType: signature.customEventType },
          { branchId },
        );
      } else if (signature.signatureType === 'direct') {
        const isOtp = signature.otp?.enabled || false;

        await generatePolicySignatureRequest({
          policyId,
          data: {
            deliveryMode: signature.deliveryMode,
            isOtp,
            phoneNumber,
          },
          params: { branchId },
        });

        if (signature.deliveryMode === 'URL' && signature.otp?.redirectUrl) {
          navigate(`/policies/${policyId}/${branchId}${signature.otp.redirectUrl}`);
        }
      }
    };
  }

  const { mutateAsync: confirmPolicy, isPending: isConfirmLoading } = useMutation({
    mutationFn: async () => {
      return policyControllerConfirmPolicy(policy.id, { branchId: workingBranch?.id! });
    },
    onSuccess: () => {
      mutate();
    },
  });

  const { mutateAsync: generatePolicySignatureRequest, isPending: isSendForSignatureLoading } =
    usePolicyControllerGeneratePolicySignatureRequest({
      mutation: {
        // biome-ignore lint/suspicious/noExplicitAny: typing problem for error
        retry: (failureCount: number, error: any) =>
          error?.errorResponse?.status === 425 && failureCount < 100,
        retryDelay: () => 1000,
      },
    });

  const { data: user } = useQuery({
    ...legacyPermissionsControllerAtMeQueryOptions(),
  });

  const brokerageFirmId = user?.brokerageFirmId!;

  const { data: brokerageFirm } = useQuery({
    ...brokerageFirmControllerGetBrokerageFirmQueryOptions(brokerageFirmId),
    enabled: !!brokerageFirmId,
    select: data => ({ ...data, customFields: keyValueFieldsToObject(data.customFields ?? []) }),
  });

  /**
   * @debt see how we handle SIGNATURE action derogation
   */
  const { data: needsDerogationCheck } = useQuery({
    ...derogationControllerCheckDerogationRequirementQueryOptions(
      workingBranch?.id!,
      {},
      {
        action: 'CONFIRM',
      },
    ),
    select: data => data.needsDerogation,
    enabled: Boolean(workingBranch?.id),
  });

  const isConfirmDerogationApproved = Boolean(
    workingBranch?.derogations?.some(
      ({ action, status }) => action === 'CONFIRM' && status === 'APPROVED',
    ),
  );

  const needsDerogation = Boolean(needsDerogationCheck) && !isConfirmDerogationApproved;

  const confirmDerogation = workingBranch?.derogations?.find(({ action }) => action === 'CONFIRM');

  const { data: documents } = useQuery({
    ...policyControllerListPolicyDocumentsQueryOptions(policy.id!, { branchId }),
    select: data => data.data,
    enabled: Boolean(policy.id),
  });

  const modalRef = useRef<OtpPhoneNumberModalRef>(null);

  const [pendingAction, setPendingAction] = useState<{
    deliveryMode: 'EMAIL' | 'URL';
    successMessage: string;
    buttonConfig?: SendContractButtonConfig;
  } | null>(null);

  const getPhoneNumber = (buttonConfig?: SendContractButtonConfig) => {
    if (buttonConfig?.actionType === 'signature') {
      const { signature } = buttonConfig;
      if (signature.signatureType === 'direct') {
        const phoneCustomField = signature.otp?.phoneCustomField;
        if (phoneCustomField && customer.customFields?.[phoneCustomField]) {
          return customer.customFields[phoneCustomField] as string;
        }
      }
    }
    return (customer.customFields?.phoneNumber as string) || '';
  };

  // A mutation, so a failed fork reaches the global error toast.
  const { mutateAsync: forkBranch } = useMutation({
    mutationFn: (branchIdToFork: string) =>
      branchControllerForkBranchWithoutPolicy(branchIdToFork, { closeOriginalBranch: true }),
  });

  const confirmIfNeeded = async () => {
    if (!needsDerogation && policy.status !== 'POLICY_CONFIRMED') {
      await confirmPolicy();
    }
  };

  const { mutateAsync: sendWithOTP } = useMutation({
    // Its API errors come from the confirm and signature mutations, which already toast them.
    meta: { disableDefaultErrorHandling: true },
    mutationFn: async (phone: string) => {
      if (!pendingAction?.buttonConfig) return;

      await confirmIfNeeded();

      const signatureRequest = await createSignatureRequest({
        policyId: policy.id,
        branchId: workingBranch?.id!,
        config: pendingAction.buttonConfig,
        generatePolicySignatureRequest,
      });
      await signatureRequest(phone);
    },
    onSuccess: () => {
      mutate();
      if (pendingAction) {
        toast.toast({ title: pendingAction.successMessage });
      }
      modalRef.current?.close();
    },
    onError: error => requoteIfInsurerChanged(error).catch(() => {}),
  });

  const handleSendWithOTP = async (phone: string) => {
    await sendWithOTP(phone);
  };

  const handleCustomButtonClick = async (buttonConfig: SendContractButtonConfig) => {
    const successMessage =
      buttonConfig.visual.successMessage ??
      t([
        `finalizeNewBusiness.buttons.${buttonConfig.id}.successMessage`,
        'finalizeNewBusiness.defaultSuccessMessage',
      ]);

    if (buttonConfig.actionType === 'confirm') {
      await confirmPolicy();
      toast.toast({ title: successMessage });
      return;
    }

    if (buttonConfig.actionType === 'signature') {
      const { signature } = buttonConfig;

      if (signature.signatureType === 'direct' && signature.otp?.enabled) {
        setPendingAction({
          deliveryMode: signature.deliveryMode,
          successMessage,
          buttonConfig,
        });
        modalRef.current?.open();
      } else {
        await confirmIfNeeded();

        const signatureRequest = await createSignatureRequest({
          policyId: policy.id,
          branchId: workingBranch?.id!,
          config: buttonConfig,
          generatePolicySignatureRequest,
        });
        await signatureRequest('');
        toast.toast({ title: successMessage });
        await mutate();
      }
    }
  };

  // A branch quoted under the previous insurer can't be re-quoted in place: re-quote a fork.
  async function requoteIfInsurerChanged(error: unknown) {
    if (getKorintErrorCode(error) !== 'INSURER_CHANGED_REQUOTE_REQUIRED' || !workingBranch) {
      throw error;
    }

    const fork = await forkBranch(workingBranch.id);
    const funnelPath = branchFunnelPath({
      branchType: workingBranch.branchType,
      productCustomPath,
      customerId: policy.customers[0],
      policyId: policy.id,
      branchId: fork.id,
    });
    navigate(funnelPath ?? `/policies/${policy.id}/${fork.id}`);
  }

  const buttonConfigs: SendContractButtonConfig[] = useMemo(() => {
    if (!('buttons' in finalizeNewBusinessModuleConfig)) {
      throw new Error('SendContractButton requires buttons configuration');
    }

    const buttons = finalizeNewBusinessModuleConfig.buttons as SendContractButtonConfig[];
    return buttons.filter(button => {
      if (button.visibilityConditions) {
        const conditions = button.visibilityConditions;

        if (conditions.firmTypes && conditions.firmTypes.length > 0) {
          if (!brokerageFirm?.firmType || !conditions.firmTypes.includes(brokerageFirm.firmType)) {
            return false;
          }
        }

        if (conditions.policyStatuses && conditions.policyStatuses.length > 0) {
          if (!conditions.policyStatuses.includes(policy.status)) {
            return false;
          }
        }

        if (conditions.branchStatuses && conditions.branchStatuses.length > 0) {
          if (
            workingBranch?.id &&
            workingBranch?.branchStatus &&
            !conditions.branchStatuses.includes(workingBranch?.branchStatus)
          ) {
            return false;
          }
        }

        if (conditions.branchTypes && conditions.branchTypes.length > 0) {
          if (
            !workingBranch?.branchType ||
            !conditions.branchTypes.includes(workingBranch.branchType)
          ) {
            return false;
          }
        }

        if (conditions.derogationStatuses && conditions.derogationStatuses.length > 0) {
          /**
           * @debt see how we handle SIGNATURE action derogation
           */
          const derogationsCondition = workingBranch?.derogations?.some(
            ({ action, status }) =>
              action === 'CONFIRM' && conditions.derogationStatuses!.includes(status),
          );

          const hasConfirmDerogation = workingBranch?.derogations?.some(
            ({ action }) => action === 'CONFIRM',
          );

          const clearedWithoutDerogation =
            needsDerogationCheck === false &&
            !hasConfirmDerogation &&
            conditions.derogationStatuses.includes('APPROVED');

          if (!derogationsCondition && !clearedWithoutDerogation) {
            return false;
          }
        }

        if (
          conditions.needsDerogation !== undefined &&
          conditions.needsDerogation !== needsDerogation
        ) {
          return false;
        }
      }

      return true;
    });
  }, [
    finalizeNewBusinessModuleConfig,
    brokerageFirm,
    policy.status,
    workingBranch,
    needsDerogation,
    needsDerogationCheck,
  ]);

  // Identity-keyed: one batched flag read covers every button's rules, which a map cannot do.
  const enabledButtonRules = new Set(
    useFeatureFlaggedRules(buttonConfigs.flatMap(buttonConfig => buttonConfig.rules ?? [])),
  );

  const isButtonSignatureWithOTP = (b: SendContractButtonConfig): boolean => {
    return (
      b.actionType === 'signature' &&
      b.signature.signatureType === 'direct' &&
      !!b.signature.otp?.redirectUrl
    );
  };

  if (!workingBranch || !customer) {
    return null;
  }

  const derogationValidationRules = [
    {
      isValid: workingBranch.branchStatus !== 'BRANCH_CLOSED',
      message: t('finalizeNewBusiness.branchClosed'),
    },
    {
      isValid: policy.status === 'POLICY_CONFIRMED',
      message: expectedPolicyStatusConflictMessage(['POLICY_CONFIRMED'], t),
    },
    {
      isValid: confirmDerogation?.status === 'CREATED',
      message: t('finalizeNewBusiness.noPendingDerogation'),
    },
  ];

  return (
    <>
      <OtpPhoneNumberModal
        ref={modalRef}
        defaultPhoneNumber={getPhoneNumber(pendingAction?.buttonConfig)}
        onSend={handleSendWithOTP}
        isLoading={isSendForSignatureLoading}
        customerPageUrl={`/policies/${policy.id}/${workingBranch?.id}/informations`}
      />
      <Group gap={2}>
        {buttonConfigs.map(buttonConfig => {
          const buttonSignatureWithOTP = isButtonSignatureWithOTP(buttonConfig);
          // own branch: its rules omit the POLICY_QUOTED gate so it stays clickable on POLICY_CONFIRMED
          if (buttonSignatureWithOTP) {
            return (
              <DeactivatableButton
                key={buttonConfig.id}
                variant="outline"
                onClick={() => handleCustomButtonClick(buttonConfig).catch(requoteIfInsurerChanged)}
                rules={[
                  ...(hasUnacknowledgedAlerts
                    ? [
                        {
                          isValid: false,
                          message: t('finalizeNewBusiness.complianceRequired'),
                        },
                      ]
                    : []),
                  ...rules.map(rule => ({
                    isValid:
                      payers !== undefined &&
                      rule.isValid({
                        policy,
                        customer,
                        customers,
                        payers,
                        documents: documents ?? [],
                        hasUnacknowledgedAlerts,
                        paymentMethods: paymentMethods ?? [],
                        brokerageFirm,
                      }),
                    message: resolveRuleMessage(rule, t),
                  })),
                  ...(buttonConfig.rules
                    ? buttonConfig.rules
                        .filter(rule => enabledButtonRules.has(rule))
                        .map(rule => ({
                          isValid:
                            payers !== undefined &&
                            rule.isValid({
                              policy,
                              customer,
                              customers,
                              payers,
                              documents: documents ?? [],
                              hasUnacknowledgedAlerts,
                              paymentMethods: paymentMethods ?? [],
                              brokerageFirm,
                            }),
                          message: resolveRuleMessage(rule, t),
                        }))
                    : []),
                ]}
                className="flex items-center gap-2"
              >
                <PenIcon className="h-4 w-4" />
                <span>
                  {buttonConfig.visual.label ??
                    t(`finalizeNewBusiness.buttons.${buttonConfig.id}.label`)}
                </span>
              </DeactivatableButton>
            );
          } else {
            const baseRules =
              needsDerogation && confirmDerogation?.status === 'CREATED'
                ? derogationValidationRules
                : createValidationRules({
                    workingBranch,
                    policy,
                    customer,
                    customers,
                    payers,
                    documents: documents ?? [],
                    hasUnacknowledgedAlerts,
                    paymentMethods,
                    rules,
                    expectedStatuses: buttonConfig.visibilityConditions?.policyStatuses ?? [
                      'POLICY_QUOTED',
                    ],
                    brokerageFirm,
                    t,
                  });

            const complianceRules =
              buttonConfig.actionType === 'signature' && hasUnacknowledgedAlerts
                ? [
                    {
                      isValid: false,
                      message: t('finalizeNewBusiness.complianceRequired'),
                    },
                  ]
                : [];

            const buttonRules = buttonConfig.rules
              ? [
                  ...baseRules,
                  ...complianceRules,
                  ...buttonConfig.rules
                    .filter(rule => enabledButtonRules.has(rule))
                    .map(rule => ({
                      isValid:
                        payers !== undefined &&
                        rule.isValid({
                          policy,
                          customer,
                          customers,
                          payers,
                          documents: documents ?? [],
                          hasUnacknowledgedAlerts,
                          paymentMethods: paymentMethods ?? [],
                          brokerageFirm,
                        }),
                      message: resolveRuleMessage(rule, t),
                    })),
                ]
              : [...baseRules, ...complianceRules];

            const icon =
              buttonConfig.visual.icon === 'mail' ? (
                <MailIcon className="h-4 w-4" />
              ) : buttonConfig.visual.icon === 'pen' ? (
                <PenIcon className="h-4 w-4" />
              ) : (
                <XIcon className="h-4 w-4" />
              );

            const isLoading =
              buttonConfig.actionType === 'confirm' ? isConfirmLoading : isSendForSignatureLoading;

            return (
              <DeactivatableButton
                key={buttonConfig.id}
                variant="ghost"
                onClick={() => handleCustomButtonClick(buttonConfig).catch(requoteIfInsurerChanged)}
                icon={icon}
                isLoading={isLoading}
                rules={buttonRules}
                info={buttonConfig.visual.info}
              >
                {buttonConfig.visual.label ??
                  t(`finalizeNewBusiness.buttons.${buttonConfig.id}.label`)}
              </DeactivatableButton>
            );
          }
        })}

        {needsDerogation &&
          policy.status === 'POLICY_CONFIRMED' &&
          (() => {
            const allButtons = finalizeNewBusinessModuleConfig.buttons ?? [];
            const getButton = () => {
              if (confirmDerogation?.status === 'CREATED') {
                return allButtons.find(b => b.actionType === 'confirm');
              }
              if (confirmDerogation?.status === 'DENIED') {
                return allButtons.find(b => b.actionType === 'decline-derogation');
              }
              return allButtons.find(b => b.actionType === 'signature');
            };
            const button = getButton();
            if (!button) return null;
            const message =
              button.visual.successMessage ??
              t(`finalizeNewBusiness.buttons.${button.id}.successMessage`);
            return (
              <Badge variant={confirmDerogation?.status === 'DENIED' ? 'destructive' : 'outline'}>
                {message}
              </Badge>
            );
          })()}

        {!needsDerogation && policy.status === 'POLICY_CONFIRMED' && (
          <Badge variant="outline">{t('finalizeNewBusiness.contractSentBadge')}</Badge>
        )}
      </Group>
    </>
  );
}
